"use client";

// Score challenge on a test page opened from a shared score link (lib/challenge.ts):
// the banner above the test, and the friend's score for the result view.
import { createContext, useContext, useEffect, type ReactNode } from "react";
import { Trophy, Users } from "lucide-react";
import { shareBanner, shareScoreText, type ChallengeInfo } from "@/lib/challenge";
import { setActiveChallenge, trackTest } from "@/lib/freeTests";
import { useT } from "@/components/i18n/I18nProvider";

const ChallengeContext = createContext<ChallengeInfo | null>(null);

/** The challenge of this page, or null (no shared score link). */
export const useChallenge = (): ChallengeInfo | null => useContext(ChallengeContext);

export function ChallengeProvider({ challenge, children }: { challenge: ChallengeInfo | null; children: ReactNode }) {
  const test = challenge?.test;
  const score = challenge?.score ?? null;
  const hasProfile = Boolean(challenge?.traits);
  useEffect(() => {
    if (!test) return;
    setActiveChallenge({ test, score });
    trackTest("free_test_challenge_landed", { test, has_score: score !== null || hasProfile });
    return () => setActiveChallenge(null);
  }, [test, score, hasProfile]);
  return <ChallengeContext.Provider value={challenge}>{children}</ChallengeContext.Provider>;
}

/** "Your friend scored 241 ms. Can you beat it?" above the test (valid scores only). */
export function ChallengeBanner() {
  const c = useChallenge();
  const tr = useT();
  if (c?.fromEmbed && (c.score !== null || c.traits)) {
    return <section id="shared-result" className="mb-6 scroll-mt-24 rounded-2xl border border-primary/30 bg-primary-light p-6 text-center" aria-label={tr.t("embed.your_result")}>
      <p className="text-sm font-semibold uppercase tracking-wide text-primary">{tr.t("embed.your_result")}</p>
      {c.score !== null && <p className="mt-3 text-3xl font-bold sm:text-4xl" data-embed-score={c.score}>{shareScoreText(tr, c.test, c.score)}</p>}
      {c.traits && <dl className="mt-4 grid gap-2">{Object.entries(c.traits).map(([trait, value]) => <div key={trait} className="flex justify-between gap-4"><dt>{tr.t(`big_five.${trait}`)}</dt><dd className="font-bold">{tr.num(value)}</dd></div>)}</dl>}
      <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-muted-foreground">{tr.t("embed.result_note")}</p>
    </section>;
  }
  if (c?.traits) {
    // A shared profile (Big Five): nothing to beat, only to compare.
    return (
      <div
        className="mb-4 flex items-center gap-3 rounded-xl border border-primary/30 bg-primary-light px-4 py-3 text-foreground"
        role="note"
      >
        <Users className="h-5 w-5 shrink-0 text-primary" aria-hidden />
        <p className="leading-snug">{tr.rich(`share.banner_${c.test}`, {}, { b: (chunk) => <strong key="b" className="font-bold">{chunk}</strong> })}</p>
      </div>
    );
  }
  if (!c || c.score === null) return null;
  const banner = shareBanner(tr, c.test, c.score);
  return (
    <div
      className="mb-4 flex items-center gap-3 rounded-xl border border-primary/30 bg-primary-light px-4 py-3 text-foreground"
      role="note"
    >
      <Trophy className="h-5 w-5 shrink-0 text-primary" aria-hidden />
      <p className="leading-snug">
        {tr.rich(banner.key, { score: banner.score }, {
          b: (chunk) => (
            <strong key="b" className="whitespace-nowrap font-bold">
              {chunk}
            </strong>
          ),
        })}
      </p>
    </div>
  );
}

/** "You: 228 ms · Your friend: 241 ms" under a result, when the page came from a challenge. */
export function ChallengeCompare({ mine }: { mine: number | null }) {
  const c = useChallenge();
  const tr = useT();
  if (!c || c.score === null || mine === null) return null;
  return (
    <p className="mt-6 text-center text-lg font-semibold text-foreground">
      <span className="tabular-nums">{tr.t("share.compare_you", { score: shareScoreText(tr, c.test, mine) })}</span>
      <span className="mx-2 text-muted-foreground" aria-hidden>
        ·
      </span>
      <span className="tabular-nums">{tr.t(c.fromEmbed ? "embed.previous_score" : "share.compare_friend", { score: shareScoreText(tr, c.test, c.score) })}</span>
    </p>
  );
}
